import React, { Dispatch, FC, memo, SetStateAction, useContext, useMemo, useState } from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import {
  createBusinessRuleTargetColumnIndex,
  normalizeBusinessRules,
  validateBusinessRulesWithContext,
} from 'src/entity/ProjectSettingsForm/ui/businessRules';
import { BusinessRulesBuilder } from 'src/entity/ProjectSettingsForm/ui/BusinessRulesBuilder';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import { setError as setGlobalError, setSuccess as setGlobalSuccess } from 'src/shared/lib/utils';
import { Button, Modal } from 'src/shared/ui';

interface ProjectBusinessRulesModalProps {
  setIsOpenBusinessRulesModal: Dispatch<SetStateAction<boolean>>;
}

export const ProjectBusinessRulesModal: FC<ProjectBusinessRulesModalProps> = memo(
  ({ setIsOpenBusinessRulesModal }) => {
    const { tableSettings, setTableSettings } = useContext(ProjectsContext);
    const [rules, setRules] = useState(tableSettings?.businessRules || []);
    const [isSaving, setIsSaving] = useState(false);

    const onClose = () => setIsOpenBusinessRulesModal(false);

    const availableColumns = useMemo(
      () =>
        (tableSettings?.formStructure || [])
          .filter(field => field.columnType !== 'actions')
          .map(field => field.columnLabel || field.columnName)
          .filter(Boolean),
      [tableSettings?.formStructure],
    );

    const targetColumnIndex = useMemo(
      () => createBusinessRuleTargetColumnIndex(tableSettings?.formStructure || []),
      [tableSettings?.formStructure],
    );

    const handleSave = async () => {
      if (!tableSettings) {
        return;
      }

      const parsedRules = normalizeBusinessRules(rules, {
        targetColumnLookup: targetColumnIndex.lookup,
      });
      const validationErrors = validateBusinessRulesWithContext(parsedRules, {
        targetColumnIndex,
      });
      if (validationErrors.length) {
        setGlobalError(`Проверка бизнес-правил: ${validationErrors[0]}`);
        return;
      }

      setIsSaving(true);

      try {
        await ProjectsService.updateForm({
          id: tableSettings.id,
          formName: tableSettings.formName,
          formDescription: tableSettings.formDescription,
          appearanceSettings: tableSettings.appearanceSettings || {},
          viewSettings: tableSettings.viewSettings || {},
          businessRules: parsedRules,
          flkRules: tableSettings.flkRules || [],
          workflowSettings: tableSettings.workflowSettings,
          currentStatus: tableSettings.workflowCurrentStatus,
        });

        setTableSettings({
          ...tableSettings,
          businessRules: parsedRules,
        });

        setGlobalSuccess('Бизнес-правила сохранены');
        onClose();
      } catch (error: any) {
        if (error?.response?.data?.error) {
          setGlobalError(error.response.data.error);
        } else {
          setGlobalError('Не удалось сохранить бизнес-правила');
        }
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Modal onClose={onClose}>
        <div className="flex w-[50rem] flex-col items-center">
          <div className="font-gothampro-500 mb-5 w-fit text-xl text-green-100">Бизнес-правила</div>

          <div className="w-full">
            <BusinessRulesBuilder
              rules={rules}
              availableColumns={availableColumns}
              onChange={setRules}
            />
          </div>

          <div className="mt-5 flex w-full items-center gap-3">
            <Button variant="green" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="greyLight" onClick={onClose}>
              Отменить
            </Button>
          </div>
        </div>
      </Modal>
    );
  },
);
